import { gql, useQuery } from '@apollo/client';
import React, { ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import { Links } from '../../lib/constants';
import { AppState } from '../../lib/store';
import { userChipResellerQuery } from '../../lib/utils';
import { gqlTypes } from '../../types/gqlTypes';

type UserType = 'reseller' | 'owner';

// `currentUser.isAdmin` is gated by `ensureAdmin` on the backend — querying it
// as a non-admin returns a Forbidden error and `data: null`. We isolate it in
// its own query (with `errorPolicy: 'all'`) so the error doesn't take down any
// other query and so we can treat a thrown error as "not admin → redirect".
const currentUserIsAdminQuery = gql`
  query currentUserIsAdmin {
    currentUser {
      isAdmin
    }
  }
`;

export default function EnsureLoggedIn({
  children,
  userType,
  loginPath,
  adminOnly,
}: {
  children: ReactNode;
  userType?: UserType;
  loginPath?: string;
  adminOnly?: boolean;
}) {
  const location = useLocation();
  const isLoggedIn = useSelector((state: AppState) => !!state.session);
  const { data: resellerData, loading: resellerLoading } = useQuery<gqlTypes.userChipReseller>(userChipResellerQuery, {
    skip: !isLoggedIn,
  });
  const { data: adminData, loading: adminLoading } = useQuery<gqlTypes.currentUserIsAdmin>(currentUserIsAdminQuery, {
    skip: !isLoggedIn || !adminOnly,
    errorPolicy: 'all',
  });

  if (!isLoggedIn) {
    if (loginPath) {
      const redirectUri = `${location.pathname}${location.search}`;
      return <Redirect to={`${loginPath}?redirectUri=${encodeURIComponent(redirectUri)}`} />;
    }
    return <Redirect to={Links.login(`${location.pathname}${location.search}`)} />;
  }

  if (userType && resellerLoading) {
    return null;
  }
  if (adminOnly && adminLoading) {
    return null;
  }

  if (adminOnly && !adminData?.currentUser?.isAdmin) {
    return <Redirect to="/" />;
  }

  const currentUserIsReseller = !!resellerData?.currentUser.chipReseller;
  if (userType === 'reseller' && !currentUserIsReseller) {
    return <Redirect to={Links.manageHome} />;
  } else if (userType === 'owner' && currentUserIsReseller) {
    return <Redirect to={Links.vetChipList()} />;
  }

  return <>{children}</>;
}

export const withEnsureLoggedIn =
  <P extends {}>(Component: React.FC<P>, userType?: UserType, loginPath?: string, adminOnly?: boolean): React.FC<P> =>
  (props: P) => {
    return (
      <EnsureLoggedIn userType={userType} loginPath={loginPath} adminOnly={adminOnly}>
        <Component {...props} />
      </EnsureLoggedIn>
    );
  };
